import I18n from 'components/I18n'
import { OverlayLoader } from 'components/Loaders'
import Money from 'components/Money'
import { actions, selectors } from 'data'
import { Bank } from 'data/Payments/BankTransfer/types'
import { BeneficiaryType, WithdrawalRequest } from 'data/types'
import messages from 'lang'
import React, { FormEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import FirstSetup2FA from 'scenes/Account/FirstSetup2FA'
import BeneficiaryButton from 'scenes/Cashflow/BeneficiaryButton'
import { Header, ModalCard, ModalTitle } from 'scenes/Cashflow/styles'
import styled from 'styled-components'
import { color } from 'utils'

import CurrencyAmountInput from '../CurrencyAmountInput'
import Fee from '../Fee'
import SecondFactorInput from '../SecondFactorInput'
import { Button } from '../styles'
import Balance from './Balance'
import Success from './Success'

const StyledFirstSetup2FA = styled(FirstSetup2FA)`
  margin-top: 1rem;
`

const ErrorLabel = styled.span`
  color: ${color('error')};
`

const Footer = styled.footer`
  margin-top: 1.5rem;
`

const getAmountError = (amount: string, currency: string, min: number, max: number) => {
  if (!amount) return null

  if (Number(amount) < min) {
    return messages.validation_min_amount
  }

  if (Number(amount) > max) {
    return messages.validation_max_amount
  }

  return null
}

type Props = {
  bank?: Bank
  beneficiary?: BeneficiaryType
  currency: string
  onSelectMethod: () => void
}
const WithdrawFormModal = ({ bank, beneficiary, currency, onSelectMethod }: Props) => {
  const dispatch = useDispatch()
  const [amount, setAmount] = useState('')
  const [secondFactor, setSecondFactor] = useState('')

  const twoFactorStatus = useSelector(selectors.auth.getTwoFactorStatus)
  const has2FASetup = twoFactorStatus && twoFactorStatus.length > 0

  const info = useSelector(selectors.payments.withdraw.getWithdrawInfo)
  const isSubmitting = useSelector(selectors.withdrawal.isSubmitting)
  const withdrawal = useSelector(selectors.withdrawal.getSubmittedOrder)
  const isFetching = useSelector(selectors.payments.withdraw.isFetching)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!beneficiary && !bank) return

    const payload: WithdrawalRequest = {
      amount: Number(amount),
      beneficiary: {
        type: beneficiary ? 'beneficiary' : 'bank',
        value: beneficiary ? beneficiary.id : bank!.id
      },
      currency,
      input_amount: amount,
      input_currency: currency,
      isFiat: true,
      secondFactor
    }

    dispatch(actions.withdrawal.submit(payload))
    dispatch(
      actions.analytics.trackEvent({
        key: 'Withdrawal Amount Entered',
        properties: {
          amount: payload.amount,
          currency: payload.currency,
          input_amount: payload.input_amount,
          input_currency: payload.input_currency,
          withdrawal_method: beneficiary ? 'BANK_ACCOUNT' : 'BANK_TRANSFER'
        }
      })
    )
  }

  useEffect(() => {
    dispatch(actions.twoFactor.getStatus())

    return () => {
      dispatch(actions.withdrawal.reset())
    }
  }, [])

  useEffect(() => {
    dispatch(actions.payments.withdraw.fetch(currency))
  }, [currency])

  const handleClose = () => dispatch(actions.app.hideModal('WITHDRAWAL'))
  if (withdrawal) return <Success amount={amount} currency={currency} onBack={handleClose} />

  if (!info || isSubmitting || isFetching) {
    return (
      <ModalCard>
        <OverlayLoader />
      </ModalCard>
    )
  }

  const pmId = beneficiary ? 'BANK_ACCOUNT' : 'BANK_TRANSFER'
  // @ts-ignore FIXME DEFAULT can be in the object, improve typing to consider that
  const paymentMethodAndCurrencyInfo = info[pmId] ?? info.DEFAULT ?? null
  if (!paymentMethodAndCurrencyInfo) return null
  const { fee, gross: balance, max, min } = paymentMethodAndCurrencyInfo
  const amountErrorMessage = getAmountError(amount, currency, min, max)
  const isDisabled =
    !amount || !!amountErrorMessage || !secondFactor || (!beneficiary && !bank) || !has2FASetup

  return (
    <ModalCard>
      <Header>
        <ModalTitle>
          <I18n data={messages.withdrawl_currency} currency={currency} />
        </ModalTitle>
      </Header>
      <Balance mainAmount={balance} mainCurrency={currency} />
      <CurrencyAmountInput
        amount={amount}
        currency={currency}
        e2e='withdrawAmount'
        onAmountChange={setAmount}
      />
      {amountErrorMessage && (
        <ErrorLabel data-e2e='withdrawAmountError'>
          <I18n
            data={amountErrorMessage}
            field={<I18n data={messages.amount} />}
            minAmount={<Money amount={min} currency={currency} />}
            maxAmount={<Money amount={max} currency={currency} />}
          />
        </ErrorLabel>
      )}
      <BeneficiaryButton
        bank={bank}
        beneficiary={beneficiary}
        e2e='withdrawSelectDepositMethodButton'
        onClick={onSelectMethod}
        title={<I18n data={messages.select_withdraw_method} />}
      />
      {has2FASetup ? (
        <SecondFactorInput onChange={setSecondFactor} value={secondFactor} />
      ) : (
        <StyledFirstSetup2FA dark />
      )}
      {fee > 0 && <Fee amount={fee} currency={currency} />}
      <Footer>
        <Button disabled={isDisabled} onClick={handleSubmit} type='submit'>
          <I18n data={messages.withdraw_title} />
        </Button>
      </Footer>
    </ModalCard>
  )
}

export default WithdrawFormModal
